import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
// ============= Execution Path Tracer Types =============
/**
 * Service call types
 */
export const ServiceCallType = z.enum([
    'internal', // Same process call
    'http', // HTTP/REST API call
    'grpc', // gRPC call
    'graphql', // GraphQL query/mutation
    'message-queue', // Message queue publish/consume
    'database', // Database query
    'cache', // Cache read/write
    'external', // External API call
]);
/**
 * Database operation types
 */
export const DatabaseOperation = z.enum([
    'select',
    'insert',
    'update',
    'delete',
    'upsert',
    'transaction',
    'raw',
]);
/**
 * ORM/Database library types
 */
export const DatabaseLibrary = z.enum([
    'prisma',
    'drizzle',
    'typeorm',
    'sequelize',
    'knex',
    'mikroorm',
    'mongoose',
    'pg',
    'mysql',
    'sqlite',
    // Rust — sqlx compile-time-checked query macros (#1449 Phase C)
    'sqlx',
    // Rust — diesel query builder + sea-orm entity API (#1449 Phase C.5)
    'diesel',
    'sea-orm',
    'redis',
    // Graph databases
    'neo4j',
    // Document databases
    'mongodb',
    // Cloud/Serverless databases
    'dynamodb',
    'firestore',
    'supabase',
    // Search engines
    'elasticsearch',
    // Other
    'cassandra',
    'unknown',
]);
// ============= Service Call Schema =============
/**
 * Represents a service-to-service call
 */
export const ServiceCallSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Type of service call */
    type: ServiceCallType,
    /** Source service/function */
    source: z.string(),
    /** Target service/endpoint */
    target: z.string(),
    /** Method/operation name */
    method: z.string().optional(),
    /** Whether the call is async */
    isAsync: z.boolean(),
    /** Timeout if specified */
    timeout: z.number().optional(),
    /** Retry configuration if any */
    retryConfig: z.object({
        maxRetries: z.number(),
        backoff: z.string().optional(),
    }).optional(),
    /** File where call is made */
    sourceFile: z.string(),
    /** Line number */
    sourceLine: z.number(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Database Query Schema =============
/**
 * Represents a database query/operation
 */
export const DatabaseQuerySchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Operation type */
    operation: DatabaseOperation,
    /** Table/collection name */
    table: z.string().optional(),
    /** Model name if using ORM */
    model: z.string().optional(),
    /** ORM/Library used */
    library: DatabaseLibrary,
    /** Raw query if available */
    rawQuery: z.string().optional(),
    /** Whether this is in a transaction */
    inTransaction: z.boolean(),
    /** Fields selected/affected */
    fields: z.array(z.string()).optional(),
    /** Where clause fields */
    whereFields: z.array(z.string()).optional(),
    /** Join information */
    joins: z.array(z.object({
        table: z.string(),
        type: z.string(),
    })).optional(),
    /**
     * Issue #898 F1: ordered list of conditional gates the call sits under,
     * captured during AST traversal. e.g. inside
     * `if (deletePermanently) { if (!owned) ... else prisma.x.delete() }`
     * the inner delete gets `['deletePermanently', '!(owned)']`. Empty/undefined
     * means the call sits at the top level of the handler.
     */
    branchPath: z.array(z.string()).optional(),
    /** File where query is made */
    sourceFile: z.string(),
    /** Line number */
    sourceLine: z.number(),
    /** Containing function/method */
    sourceContext: z.string().optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= External API Call Schema =============
/**
 * Represents an external API call (third-party services)
 */
export const ExternalApiCallSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Service name (e.g., "stripe", "twilio", "sendgrid") */
    service: z.string(),
    /** API endpoint/method */
    endpoint: z.string(),
    /** HTTP method if applicable */
    method: z.string().optional(),
    /** SDK/library used */
    library: z.string().optional(),
    /** Issue #898 F1: see DatabaseQuerySchema.branchPath. */
    branchPath: z.array(z.string()).optional(),
    /** Whether this is a sandbox/test call */
    isSandbox: z.boolean().optional(),
    /** Rate limit info if detected */
    rateLimit: z.object({
        limit: z.number().optional(),
        window: z.string().optional(),
    }).optional(),
    /** File where call is made */
    sourceFile: z.string(),
    /** Line number */
    sourceLine: z.number(),
    /** Containing function/method */
    sourceContext: z.string().optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Execution Step Schema =============
/**
 * Single step in an execution path
 */
export const ExecutionStepSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Step order (0-indexed) */
    order: z.number(),
    /** Step type */
    type: z.enum([
        'function-call',
        'service-call',
        'database-query',
        'external-api',
        'cache-operation',
        'message-publish',
        'message-consume',
        'state-change',
    ]),
    /** Name/description of the step */
    name: z.string(),
    /** Detailed description */
    description: z.string().optional(),
    /** Related service call */
    serviceCall: ServiceCallSchema.optional(),
    /** Related database query */
    databaseQuery: DatabaseQuerySchema.optional(),
    /** Related external API call */
    externalApiCall: ExternalApiCallSchema.optional(),
    /** Input data shape */
    input: z.unknown().optional(),
    /** Output data shape */
    output: z.unknown().optional(),
    /** Error handling info */
    errorHandling: z.object({
        hasTryCatch: z.boolean(),
        errorTypes: z.array(z.string()),
    }).optional(),
    /** Timing/performance hints */
    timing: z.object({
        estimatedMs: z.number().optional(),
        isBlocking: z.boolean(),
    }).optional(),
    /** File where step occurs */
    sourceFile: z.string(),
    /** Line number */
    sourceLine: z.number(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Execution Path Schema =============
/**
 * Complete execution path from entry point to completion
 */
export const ExecutionPathSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Path name/description */
    name: z.string(),
    /** Entry point (e.g., API route handler) */
    entryPoint: z.object({
        type: z.enum(['api-route', 'event-handler', 'cron-job', 'message-consumer', 'function']),
        name: z.string(),
        file: z.string(),
        line: z.number(),
    }),
    /** Ordered execution steps */
    steps: z.array(ExecutionStepSchema),
    /** Exit points (return statements, throws) */
    exitPoints: z.array(z.object({
        type: z.enum(['return', 'throw', 'redirect']),
        statusCode: z.number().optional(),
        file: z.string(),
        line: z.number(),
    })),
    /** Total estimated duration */
    estimatedDurationMs: z.number().optional(),
    /** Services touched */
    touchedServices: z.array(z.string()),
    /** Tables/collections accessed */
    touchedTables: z.array(z.string()),
    /** External APIs called */
    touchedExternalApis: z.array(z.string()),
    /** Path complexity score */
    complexity: z.number().optional(),
});
// ============= Execution Path Tracer Result =============
/**
 * Complete result from execution path tracing
 */
export const ExecutionPathTracerResultSchema = z.object({
    /** All traced execution paths */
    paths: z.array(ExecutionPathSchema),
    /** All detected service calls */
    serviceCalls: z.array(ServiceCallSchema),
    /** All detected database queries */
    databaseQueries: z.array(DatabaseQuerySchema),
    /** All detected external API calls */
    externalApiCalls: z.array(ExternalApiCallSchema),
    /** Summary statistics */
    summary: z.object({
        totalPaths: z.number(),
        totalSteps: z.number(),
        totalServiceCalls: z.number(),
        totalDatabaseQueries: z.number(),
        totalExternalApiCalls: z.number(),
        uniqueServices: z.number(),
        uniqueTables: z.number(),
        uniqueExternalApis: z.number(),
        averagePathLength: z.number(),
        maxPathLength: z.number(),
    }),
});
//# sourceMappingURL=executionPathTracer.js.map